import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/cn';

const buttonVariants = cva(
  // 44 px touch targets in the buyer portal (docs/06 §9) — hence `h-11` on the default size.
  // The admin is a desk app and uses `sm` (36 px) almost everywhere; both live here so the two
  // audiences cannot drift into two different buttons.
  [
    'inline-flex select-none items-center justify-center gap-1.5 whitespace-nowrap rounded-lg',
    'text-sm font-medium transition-[background-color,border-color,box-shadow,transform,filter] duration-150',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
    'disabled:pointer-events-none disabled:opacity-50',
    // A 1px press. It is the cheapest possible "the click registered", and on a screen where
    // every button fires a mutation that is worth having.
    'active:translate-y-px',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
  ].join(' '),
  {
    variants: {
      variant: {
        // The filled variants are top-lit: a white sheen fading down the face, a hairline inner
        // highlight along the top edge, and an inset keyline in the text ink instead of a drop
        // shadow — depth without violating the "elevation is borders and surface tone" rule.
        // Hover deepens the fill itself (brightness/saturation) rather than fading opacity,
        // which would wash the yellow out against the parchment canvas. The press flattens the
        // sheen into a shallow inner shadow, so the button visibly sinks.
        default: [
          'bg-primary text-primary-foreground',
          'bg-gradient-to-b from-white/30 to-white/0',
          'shadow-[inset_0_1px_0_0_hsl(0_0%_100%/0.5),inset_0_0_0_1px_hsl(var(--primary-foreground)/0.12)]',
          'hover:brightness-[0.97] hover:saturate-[1.15]',
          'active:bg-none active:shadow-[inset_0_0_0_1px_hsl(var(--primary-foreground)/0.14),inset_0_1px_3px_0_hsl(var(--primary-foreground)/0.14)]',
        ].join(' '),
        destructive: [
          'bg-destructive text-destructive-foreground',
          'bg-gradient-to-b from-white/15 to-white/0',
          'shadow-[inset_0_1px_0_0_hsl(0_0%_100%/0.25),inset_0_0_0_1px_hsl(0_0%_0%/0.15)]',
          'hover:brightness-95',
          'active:bg-none active:shadow-[inset_0_1px_3px_0_hsl(0_0%_0%/0.25)]',
        ].join(' '),
        // The hover border is the same `muted-foreground/40` the Input's `fieldBase` uses, so an
        // outline button and the field beside it darken by the same amount under the cursor.
        outline:
          'border border-input bg-background shadow-card hover:border-muted-foreground/40 hover:bg-muted',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/70',
        ghost: 'hover:bg-muted hover:text-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-11 px-4 py-2',
        sm: 'h-9 px-3',
        xs: 'h-8 px-2.5 text-xs',
        lg: 'h-12 px-8 text-base',
        icon: 'h-11 w-11',
        'icon-sm': 'h-9 w-9',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size }), className)} ref={ref} {...props} />
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
